/**
 * Deal Room Templates Page
 * 
 * Enterprise-grade management interface for Deal Room Templates.
 * Templates define pre-configured layouts for collaborative deal workspaces
 * including sections, permissions, branding, and access controls.
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  IconButton,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  FormControlLabel,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as PreviewIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  ArrowForward as NextIcon,
  ArrowBack as BackIcon,
  Settings as SettingsIcon,
  Palette as PaletteIcon,
  Security as SecurityIcon,
  ViewModule as SectionsIcon,
} from '@mui/icons-material';
import {
  getDealRoomTemplates,
  createDealRoomTemplate,
  updateDealRoomTemplate,
} from '../api/dealRoomApi';
import type {
  DealRoomTemplate,
  DealRoomTemplateCreate,
  DealRoomSection,
  DealRoomPermissions,
  DealRoomBranding,
  DealRoomAccessLevel,
} from '../types/dealRoom';

const SECTION_TYPES = [
  { value: 'overview', label: 'Overview', icon: '📋' },
  { value: 'documents', label: 'Documents', icon: '📄' },
  { value: 'chat', label: 'Chat', icon: '💬' },
  { value: 'tasks', label: 'Tasks', icon: '✅' },
  { value: 'timeline', label: 'Timeline', icon: '📅' },
  { value: 'custom', label: 'Custom', icon: '⚙️' },
] as const;

const ACCESS_LEVELS = [
  { value: 'read_only', label: 'Read Only', description: 'Can view content only' },
  { value: 'comment', label: 'Comment', description: 'Can view and comment' },
  { value: 'edit', label: 'Edit', description: 'Can view, comment, and edit' },
  { value: 'admin', label: 'Admin', description: 'Full control including access management' },
] as const;

export const DealRoomTemplatesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<DealRoomTemplate | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<DealRoomTemplate | null>(null);

  // Fetch templates
  const { data: templates, isLoading } = useQuery({
    queryKey: ['dealRoomTemplates'],
    queryFn: () => getDealRoomTemplates(true),
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: createDealRoomTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dealRoomTemplates'] });
      setCreateDialogOpen(false);
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: any }) =>
      updateDealRoomTemplate(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dealRoomTemplates'] });
      setEditingTemplate(null);
    },
  });

  const handleCreateTemplate = () => {
    setEditingTemplate(null);
    setCreateDialogOpen(true);
  };

  const handleEditTemplate = (template: DealRoomTemplate) => {
    setEditingTemplate(template);
    setCreateDialogOpen(true);
  };

  const handlePreviewTemplate = (template: DealRoomTemplate) => {
    setPreviewTemplate(template);
  };

  const handleCloseDialog = () => {
    setCreateDialogOpen(false);
    setEditingTemplate(null);
  };

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Typography>Loading templates...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Deal Room Templates
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Configure pre-built templates for collaborative deal workspaces with sections, permissions, and branding.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateTemplate}
          size="large"
        >
          Create Template
        </Button>
      </Box>

      {/* Templates Grid */}
      <Grid container spacing={3}>
        {templates?.map((template) => (
          <Grid item xs={12} md={6} lg={4} key={template.id}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography variant="h6" gutterBottom>
                    {template.name}
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {template.is_default && (
                      <Chip label="Default" color="primary" size="small" />
                    )}
                    {template.is_active ? (
                      <Chip label="Active" color="success" size="small" />
                    ) : (
                      <Chip label="Inactive" color="default" size="small" />
                    )}
                  </Stack>
                </Box>

                {template.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {template.description}
                  </Typography>
                )}

                <Divider sx={{ my: 2 }} />

                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    SECTIONS ({template.sections.length})
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {template.sections.slice(0, 4).map((section) => {
                      const sectionType = SECTION_TYPES.find(t => t.value === section.type);
                      return (
                        <Chip
                          key={section.id}
                          label={`${sectionType?.icon || ''} ${section.title}`}
                          size="small"
                          variant="outlined"
                          sx={{ mb: 0.5 }}
                        />
                      );
                    })}
                    {template.sections.length > 4 && (
                      <Chip
                        label={`+${template.sections.length - 4} more`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Stack>
                </Box>

                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    DEFAULT ACCESS
                  </Typography>
                  <Chip
                    label={template.default_permissions.default_access_level.replace('_', ' ').toUpperCase()}
                    size="small"
                    color="info"
                  />
                </Box>

                <Box>
                  <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                    BRANDING
                  </Typography>
                  <Typography variant="body2">
                    {template.branding.company_name || 'No branding configured'}
                  </Typography>
                </Box>
              </CardContent>

              <CardActions>
                <Button
                  size="small"
                  startIcon={<PreviewIcon />}
                  onClick={() => handlePreviewTemplate(template)}
                >
                  Preview
                </Button>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleEditTemplate(template)}
                >
                  Edit
                </Button>
                <IconButton size="small" title="Duplicate template">
                  <CopyIcon fontSize="small" />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Create/Edit Dialog */}
      {createDialogOpen && (
        <TemplateWizardDialog
          open={createDialogOpen}
          onClose={handleCloseDialog}
          template={editingTemplate}
          onSave={(template) => {
            if (editingTemplate) {
              updateMutation.mutate({ id: editingTemplate.id, updates: template });
            } else {
              createMutation.mutate(template);
            }
          }}
        />
      )}

      {/* Preview Dialog */}
      {previewTemplate && (
        <TemplatePreviewDialog
          template={previewTemplate}
          open={!!previewTemplate}
          onClose={() => setPreviewTemplate(null)}
        />
      )}
    </Container>
  );
};

/**
 * Multi-step wizard for creating/editing Deal Room Templates
 */
interface TemplateWizardDialogProps {
  open: boolean;
  onClose: () => void;
  template: DealRoomTemplate | null;
  onSave: (template: DealRoomTemplateCreate) => void;
}

const TemplateWizardDialog: React.FC<TemplateWizardDialogProps> = ({
  open,
  onClose,
  template,
  onSave,
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<DealRoomTemplateCreate>({
    name: template?.name || '',
    description: template?.description || '',
    sections: template?.sections || [],
    default_permissions: template?.default_permissions || {
      default_access_level: 'read_only' as DealRoomAccessLevel,
      allow_public_access: false,
      require_authentication: true,
      allow_guest_access: false,
      allow_downloads: true,
      allow_comments: true,
      allow_sharing: false,
    },
    branding: template?.branding || {},
    is_default: template?.is_default || false,
    is_active: template?.is_active ?? true,
  });

  const steps = ['Basic Info', 'Sections', 'Permissions', 'Branding'];

  const handleNext = () => {
    if (activeStep === steps.length - 1) {
      onSave(formData);
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const updateFormData = (updates: Partial<DealRoomTemplateCreate>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {template ? 'Edit Template' : 'Create Deal Room Template'}
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4, mt: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && (
          <BasicInfoStep formData={formData} updateFormData={updateFormData} />
        )}
        {activeStep === 1 && (
          <SectionsStep formData={formData} updateFormData={updateFormData} />
        )}
        {activeStep === 2 && (
          <PermissionsStep formData={formData} updateFormData={updateFormData} />
        )}
        {activeStep === 3 && (
          <BrandingStep formData={formData} updateFormData={updateFormData} />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} startIcon={<BackIcon />}>
            Back
          </Button>
        )}
        <Button
          onClick={handleNext}
          variant="contained"
          endIcon={activeStep === steps.length - 1 ? <CheckIcon /> : <NextIcon />}
        >
          {activeStep === steps.length - 1 ? 'Save Template' : 'Next'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Step 1: Basic Information
 */
interface StepProps {
  formData: DealRoomTemplateCreate;
  updateFormData: (updates: Partial<DealRoomTemplateCreate>) => void;
}

const BasicInfoStep: React.FC<StepProps> = ({ formData, updateFormData }) => {
  return (
    <Stack spacing={3}>
      <Alert severity="info">
        Configure basic template information. This template will be used to quickly set up new Deal Rooms with consistent settings.
      </Alert>

      <TextField
        label="Template Name"
        fullWidth
        required
        value={formData.name}
        onChange={(e) => updateFormData({ name: e.target.value })}
        placeholder="e.g., Enterprise Deal, Strategic Partnership"
      />

      <TextField
        label="Description"
        fullWidth
        multiline
        rows={3}
        value={formData.description}
        onChange={(e) => updateFormData({ description: e.target.value })}
        placeholder="Describe when this template should be used..."
      />

      <FormControlLabel
        control={
          <Switch
            checked={formData.is_default}
            onChange={(e) => updateFormData({ is_default: e.target.checked })}
          />
        }
        label="Set as default template"
      />

      <FormControlLabel
        control={
          <Switch
            checked={formData.is_active}
            onChange={(e) => updateFormData({ is_active: e.target.checked })}
          />
        }
        label="Active (available for use)"
      />
    </Stack>
  );
};

/**
 * Step 2: Sections Configuration
 */
const SectionsStep: React.FC<StepProps> = ({ formData, updateFormData }) => {
  const [newSection, setNewSection] = useState<Partial<DealRoomSection>>({
    type: 'overview',
    title: '',
    order: formData.sections.length,
    is_visible: true,
    is_required: false,
  });

  const addSection = () => {
    if (!newSection.title) return;

    const section: DealRoomSection = {
      id: `section-${Date.now()}`,
      title: newSection.title,
      type: (newSection.type as DealRoomSection['type']) || 'custom',
      order: newSection.order || formData.sections.length,
      is_visible: newSection.is_visible ?? true,
      is_required: newSection.is_required ?? false,
      description: newSection.description,
    };

    updateFormData({ sections: [...formData.sections, section] });
    setNewSection({
      type: 'overview',
      title: '',
      order: formData.sections.length + 1,
      is_visible: true,
      is_required: false,
    });
  };

  const removeSection = (id: string) => {
    updateFormData({
      sections: formData.sections.filter((s) => s.id !== id),
    });
  };

  return (
    <Stack spacing={3}>
      <Alert severity="info">
        Configure sections that will appear in Deal Rooms. Sections organize content and define the Deal Room structure.
      </Alert>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Add New Section
        </Typography>
        <Stack spacing={2}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Section Type</InputLabel>
                <Select
                  value={newSection.type}
                  label="Section Type"
                  onChange={(e) => setNewSection({ ...newSection, type: e.target.value as DealRoomSection['type'] })}
                >
                  {SECTION_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.icon} {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                size="small"
                label="Section Title"
                fullWidth
                value={newSection.title}
                onChange={(e) => setNewSection({ ...newSection, title: e.target.value })}
                placeholder="e.g., Contract Documents"
              />
            </Grid>
          </Grid>

          <TextField
            size="small"
            label="Description (optional)"
            fullWidth
            value={newSection.description || ''}
            onChange={(e) => setNewSection({ ...newSection, description: e.target.value })}
          />

          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={newSection.is_visible}
                  onChange={(e) => setNewSection({ ...newSection, is_visible: e.target.checked })}
                />
              }
              label="Visible"
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={newSection.is_required}
                  onChange={(e) => setNewSection({ ...newSection, is_required: e.target.checked })}
                />
              }
              label="Required"
            />
          </Stack>

          <Button variant="outlined" startIcon={<AddIcon />} onClick={addSection}>
            Add Section
          </Button>
        </Stack>
      </Paper>

      <Typography variant="subtitle2">
        Current Sections ({formData.sections.length})
      </Typography>
      <List>
        {formData.sections.map((section) => {
          const sectionType = SECTION_TYPES.find((t) => t.value === section.type);
          return (
            <ListItem key={section.id} divider>
              <ListItemText
                primary={`${sectionType?.icon || ''} ${section.title}`}
                secondary={section.description || `${section.type} section`}
              />
              <ListItemSecondaryAction>
                <Stack direction="row" spacing={1} alignItems="center">
                  {section.is_required && <Chip label="Required" size="small" color="error" />}
                  {!section.is_visible && <Chip label="Hidden" size="small" />}
                  <IconButton edge="end" onClick={() => removeSection(section.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </ListItemSecondaryAction>
            </ListItem>
          );
        })}
        {formData.sections.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
            No sections added yet. Add at least one section to continue.
          </Typography>
        )}
      </List>
    </Stack>
  );
};

/**
 * Step 3: Permissions Configuration
 */
const PermissionsStep: React.FC<StepProps> = ({ formData, updateFormData }) => {
  const updatePermissions = (updates: Partial<DealRoomPermissions>) => {
    updateFormData({
      default_permissions: { ...formData.default_permissions, ...updates },
    });
  };

  return (
    <Stack spacing={3}>
      <Alert severity="info">
        Configure default access permissions for Deal Rooms created with this template. These can be customized per deal.
      </Alert>

      <FormControl fullWidth>
        <InputLabel>Default Access Level</InputLabel>
        <Select
          value={formData.default_permissions.default_access_level}
          label="Default Access Level"
          onChange={(e) => updatePermissions({ default_access_level: e.target.value as DealRoomAccessLevel })}
        >
          {ACCESS_LEVELS.map((level) => (
            <MenuItem key={level.value} value={level.value}>
              <Box>
                <Typography variant="body1">{level.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {level.description}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Divider />

      <Typography variant="subtitle2">Access Controls</Typography>

      <FormControlLabel
        control={
          <Switch
            checked={formData.default_permissions.allow_public_access}
            onChange={(e) => updatePermissions({ allow_public_access: e.target.checked })}
          />
        }
        label="Allow public access (no authentication required)"
      />

      <FormControlLabel
        control={
          <Switch
            checked={formData.default_permissions.require_authentication}
            onChange={(e) => updatePermissions({ require_authentication: e.target.checked })}
          />
        }
        label="Require authentication"
      />

      <FormControlLabel
        control={
          <Switch
            checked={formData.default_permissions.allow_guest_access}
            onChange={(e) => updatePermissions({ allow_guest_access: e.target.checked })}
          />
        }
        label="Allow guest access (with access code)"
      />

      <Divider />

      <Typography variant="subtitle2">Capabilities</Typography>

      <FormControlLabel
        control={
          <Switch
            checked={formData.default_permissions.allow_downloads}
            onChange={(e) => updatePermissions({ allow_downloads: e.target.checked })}
          />
        }
        label="Allow document downloads"
      />

      <FormControlLabel
        control={
          <Switch
            checked={formData.default_permissions.allow_comments}
            onChange={(e) => updatePermissions({ allow_comments: e.target.checked })}
          />
        }
        label="Allow comments and chat"
      />

      <FormControlLabel
        control={
          <Switch
            checked={formData.default_permissions.allow_sharing}
            onChange={(e) => updatePermissions({ allow_sharing: e.target.checked })}
          />
        }
        label="Allow users to share access"
      />

      <TextField
        type="number"
        label="Access Expiry (days)"
        helperText="Optional: Number of days until access expires"
        value={formData.default_permissions.access_expiry_days || ''}
        onChange={(e) =>
          updatePermissions({
            access_expiry_days: e.target.value ? parseInt(e.target.value) : undefined,
          })
        }
      />
    </Stack>
  );
};

/**
 * Step 4: Branding Configuration
 */
const BrandingStep: React.FC<StepProps> = ({ formData, updateFormData }) => {
  const updateBranding = (updates: Partial<DealRoomBranding>) => {
    updateFormData({
      branding: { ...formData.branding, ...updates },
    });
  };

  return (
    <Stack spacing={3}>
      <Alert severity="info">
        Configure visual branding for Deal Rooms. Customize colors, logos, and messaging to match your company brand.
      </Alert>

      <TextField
        label="Company Name"
        fullWidth
        value={formData.branding.company_name || ''}
        onChange={(e) => updateBranding({ company_name: e.target.value })}
        placeholder="Your Company Name"
      />

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            label="Primary Color"
            fullWidth
            type="color"
            value={formData.branding.primary_color || '#1976d2'}
            onChange={(e) => updateBranding({ primary_color: e.target.value })}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Secondary Color"
            fullWidth
            type="color"
            value={formData.branding.secondary_color || '#dc004e'}
            onChange={(e) => updateBranding({ secondary_color: e.target.value })}
          />
        </Grid>
      </Grid>

      <TextField
        label="Logo URL"
        fullWidth
        value={formData.branding.logo_url || ''}
        onChange={(e) => updateBranding({ logo_url: e.target.value })}
        placeholder="https://example.com/logo.png"
      />

      <TextField
        label="Background Image URL"
        fullWidth
        value={formData.branding.background_image_url || ''}
        onChange={(e) => updateBranding({ background_image_url: e.target.value })}
        placeholder="https://example.com/background.jpg"
      />

      <TextField
        label="Header Text"
        fullWidth
        value={formData.branding.header_text || ''}
        onChange={(e) => updateBranding({ header_text: e.target.value })}
        placeholder="Welcome to our Deal Room"
      />

      <TextField
        label="Welcome Message"
        fullWidth
        multiline
        rows={3}
        value={formData.branding.welcome_message || ''}
        onChange={(e) => updateBranding({ welcome_message: e.target.value })}
        placeholder="Enter a welcome message for deal participants..."
      />

      <TextField
        label="Footer Text"
        fullWidth
        value={formData.branding.footer_text || ''}
        onChange={(e) => updateBranding({ footer_text: e.target.value })}
        placeholder="© 2024 Your Company. All rights reserved."
      />

      <TextField
        label="Custom CSS (Advanced)"
        fullWidth
        multiline
        rows={4}
        value={formData.branding.custom_css || ''}
        onChange={(e) => updateBranding({ custom_css: e.target.value })}
        placeholder=".deal-room { /* custom styles */ }"
        helperText="Advanced: Add custom CSS for additional styling"
      />
    </Stack>
  );
};

/**
 * Preview Dialog
 */
interface TemplatePreviewDialogProps {
  template: DealRoomTemplate;
  open: boolean;
  onClose: () => void;
}

const TemplatePreviewDialog: React.FC<TemplatePreviewDialogProps> = ({
  template,
  open,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Template Preview: {template.name}</Typography>
          <Stack direction="row" spacing={1}>
            {template.is_default && <Chip label="Default" color="primary" size="small" />}
            {template.is_active && <Chip label="Active" color="success" size="small" />}
          </Stack>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 2 }}>
          <Tab icon={<SectionsIcon />} label="Sections" />
          <Tab icon={<SecurityIcon />} label="Permissions" />
          <Tab icon={<PaletteIcon />} label="Branding" />
        </Tabs>

        {activeTab === 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Sections ({template.sections.length})
            </Typography>
            <Grid container spacing={2}>
              {template.sections.map((section) => {
                const sectionType = SECTION_TYPES.find((t) => t.value === section.type);
                return (
                  <Grid item xs={12} md={6} key={section.id}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle1">
                        {sectionType?.icon} {section.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {section.type.toUpperCase()}
                      </Typography>
                      {section.description && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {section.description}
                        </Typography>
                      )}
                      <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                        {section.is_required && <Chip label="Required" size="small" color="error" />}
                        {section.is_visible && <Chip label="Visible" size="small" color="success" />}
                      </Stack>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {activeTab === 1 && (
          <Stack spacing={2}>
            <Typography variant="subtitle2">Default Permissions</Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Default Access Level"
                    secondary={template.default_permissions.default_access_level.replace('_', ' ').toUpperCase()}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Public Access" />
                  <Chip
                    label={template.default_permissions.allow_public_access ? 'Enabled' : 'Disabled'}
                    size="small"
                    color={template.default_permissions.allow_public_access ? 'success' : 'default'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Require Authentication" />
                  <Chip
                    label={template.default_permissions.require_authentication ? 'Yes' : 'No'}
                    size="small"
                    color={template.default_permissions.require_authentication ? 'success' : 'default'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Allow Downloads" />
                  <Chip
                    label={template.default_permissions.allow_downloads ? 'Yes' : 'No'}
                    size="small"
                    color={template.default_permissions.allow_downloads ? 'success' : 'default'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Allow Comments" />
                  <Chip
                    label={template.default_permissions.allow_comments ? 'Yes' : 'No'}
                    size="small"
                    color={template.default_permissions.allow_comments ? 'success' : 'default'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemText primary="Allow Sharing" />
                  <Chip
                    label={template.default_permissions.allow_sharing ? 'Yes' : 'No'}
                    size="small"
                    color={template.default_permissions.allow_sharing ? 'success' : 'default'}
                  />
                </ListItem>
              </List>
            </Paper>
          </Stack>
        )}

        {activeTab === 2 && (
          <Stack spacing={2}>
            <Typography variant="subtitle2">Branding Configuration</Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                backgroundColor: template.branding.primary_color || '#1976d2',
                color: 'white',
                backgroundImage: template.branding.background_image_url
                  ? `url(${template.branding.background_image_url})`
                  : undefined,
                backgroundSize: 'cover',
              }}
            >
              {template.branding.logo_url && (
                <img
                  src={template.branding.logo_url}
                  alt="Logo"
                  style={{ maxHeight: '60px', marginBottom: '16px' }}
                />
              )}
              <Typography variant="h5" gutterBottom>
                {template.branding.header_text || template.branding.company_name || 'Deal Room'}
              </Typography>
              <Typography variant="body1">
                {template.branding.welcome_message || 'Welcome to the Deal Room'}
              </Typography>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <List dense>
                <ListItem>
                  <ListItemText primary="Company Name" secondary={template.branding.company_name || 'Not set'} />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Primary Color"
                    secondary={
                      <Box
                        sx={{
                          width: 40,
                          height: 20,
                          backgroundColor: template.branding.primary_color || '#1976d2',
                          border: '1px solid #ccc',
                        }}
                      />
                    }
                  />
                </ListItem>
                <ListItem>
                  <ListItemText
                    primary="Secondary Color"
                    secondary={
                      <Box
                        sx={{
                          width: 40,
                          height: 20,
                          backgroundColor: template.branding.secondary_color || '#dc004e',
                          border: '1px solid #ccc',
                        }}
                      />
                    }
                  />
                </ListItem>
                {template.branding.footer_text && (
                  <ListItem>
                    <ListItemText primary="Footer Text" secondary={template.branding.footer_text} />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DealRoomTemplatesPage;
